import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Context, ReactNode } from "react";
import { I18nextProvider } from "react-i18next";

import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  detectLanguage,
  directionFor,
  getI18n,
  type LanguageCode,
} from "@/i18n/config";

type LanguageContextValue = {
  language: LanguageCode;
  dir: "ltr" | "rtl";
  setLanguage: (code: LanguageCode) => void;
  languages: typeof SUPPORTED_LANGUAGES;
};

// Keep one context instance across live reloads so the provider and its users
// never end up holding two different copies (which blanks the page).
const CTX_KEY = "__swlLanguageContext";
const globalStore = globalThis as unknown as Record<string, unknown>;
const LanguageContext =
  (globalStore[CTX_KEY] as Context<LanguageContextValue | null> | undefined) ??
  (globalStore[CTX_KEY] = createContext<LanguageContextValue | null>(null)) as Context<LanguageContextValue | null>;

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used inside <LanguageProvider>");
  return ctx;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const i18n = getI18n();
  // SSR and the first client render always use the default language so hydration matches;
  // the detected/stored preference is applied in the effect below.
  const [language, setLanguageState] = useState<LanguageCode>(DEFAULT_LANGUAGE);

  const applyLanguage = useCallback(
    (code: LanguageCode) => {
      setLanguageState(code);
      void i18n.changeLanguage(code);
      if (typeof document !== "undefined") {
        const dir = directionFor(code);
        document.documentElement.lang = code;
        document.documentElement.dir = dir;
        document.documentElement.classList.toggle("rtl", dir === "rtl");
      }
    },
    [i18n],
  );

  useEffect(() => {
    applyLanguage(detectLanguage());
  }, [applyLanguage]);

  const setLanguage = useCallback(
    (code: LanguageCode) => {
      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
      } catch {
        /* storage blocked — preference just won't persist */
      }
      applyLanguage(code);
    },
    [applyLanguage],
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ language, dir: directionFor(language), setLanguage, languages: SUPPORTED_LANGUAGES }),
    [language, setLanguage],
  );

  return (
    <I18nextProvider i18n={i18n}>
      <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
    </I18nextProvider>
  );
}
